import { FSA_SPENDER_BALANCE_STORAGE_KEY } from "~/hec/components/fsa-funds/use-balance";
import { safeLocalStorage } from "~/lib/utils/safe-storage";

/**
 * THE client-side Segment identity reset (jam-4 rounds 2–4 — one reset,
 * every consumer: `<SegmentLogoutReset />`'s submit-start latch + backstop,
 * and `handleForcedLogout` for fetcher-driven teardowns).
 *
 * Round-4 P1 (JP): the reset must NOT depend on the Segment SDK. With
 * `window.analytics` absent (WDH / editor) the old instance-only reset
 * cleared nothing; with the SDK load BLOCKED (consent/ad-block) `reset()`
 * sat buffered until navigation destroyed the queue — either way
 * `ajs_user_id` (+ its localStorage twin) survived and `resolveIdentity`
 * kept treating the guest as the former customer. This module is OUR code
 * and runs on every logout path regardless, so it wipes Segment's storage
 * SYNCHRONOUSLY first — the identity guarantee no longer depends on the SDK
 * at all — then still calls `reset()` for the LOADED case: that clears the
 * SDK's in-memory user (which would otherwise re-persist the identity we
 * just wiped on its next event) and emits `reset` for the D6 sender's belt;
 * when merely buffered, the replay at load is a harmless belt.
 *
 * Residual (shrunk by this round): JS-DISABLED logouts only — the /logout
 * form POST works without JS, and no client code runs to clear analytics.js
 * storage. Future hardening for that last mile (JP's option 2, deliberately
 * not built this round): a server-readable logout marker that makes
 * `resolveIdentity` ignore `ajs_*` until the client confirms the wipe.
 */

/**
 * The exact CURRENT storage keys the INSTALLED `@segment/analytics-next`
 * persists identity under (dist/cjs/core/user/index.js — `ajs_user_id`,
 * `ajs_user_traits` at :11/:15, `anonKey = 'ajs_anonymous_id'` at :77, plus
 * the group pair at :173-176). Its UniversalStorage writes each key to BOTH
 * a cookie and localStorage (JSON-stringified), so the wipe must hit both.
 */
export const SEGMENT_IDENTITY_STORAGE_KEYS = [
  "ajs_user_id",
  "ajs_user_traits",
  "ajs_anonymous_id",
  "ajs_group_id",
  "ajs_group_properties",
] as const;

/**
 * LEGACY stores the SDK still READS — and neither its own `reset()`
 * (dist/cjs/core/user/index.js:117-121 clears only idKey/anonKey/traitsKey)
 * nor a current-keys-only wipe touches, so a post-logout SDK init would
 * RESURRECT the former identity from them (jam-4 round 5 P1):
 *   - `ajs_user` (`defaults.cookie.oldKey`, :12): the constructor MIGRATES
 *     it on every init — `legacyUser.id && this.id(legacyUser.id)`
 *     (:83-87), re-persisting the old id into `ajs_user_id`; `id()` also
 *     falls back to it directly (:39-40).
 *   - `_sio` (:91-97, `legacySIO()`): `"anon----user"`, split on `----`;
 *     `anonymousId()` falls back to its first half (:48).
 * Both are read through `legacyUserStore`, which is COOKIE-ONLY
 * (`createStorage(..., s => s === StoreType.Cookie)`, :81) — the wipe still
 * clears the localStorage half too (belt; a never-set key is a no-op).
 */
export const SEGMENT_LEGACY_IDENTITY_STORAGE_KEYS = [
  "ajs_user",
  "_sio",
] as const;

const ALL_SEGMENT_IDENTITY_KEYS = [
  ...SEGMENT_IDENTITY_STORAGE_KEYS,
  ...SEGMENT_LEGACY_IDENTITY_STORAGE_KEYS,
] as const;

/**
 * Cookie DOMAIN variants a Segment cookie may live under. analytics-next
 * sets cookies with `path: '/'` and `domain: tld(location.href)` — the top
 * registrable domain found by a writable-probe over the hostname's levels
 * (dist/cjs/core/user/tld.js `levels()`: for `hsa.devhec.com` that's
 * `devhec.com` then `hsa.devhec.com`; on production `fsastore.com` — a
 * DIFFERENT apex than dev). A deletion only takes effect when its
 * domain+path match how the cookie was set, so expire every level PLUS the
 * host-only form (`undefined`) — expiring a variant that was never set is a
 * no-op. IPs and single-label hosts (localhost) get host-only, mirroring
 * `levels()`' own guards.
 */
export function segmentCookieDomainVariants(
  hostname: string,
): (string | undefined)[] {
  const variants: (string | undefined)[] = [undefined];
  const parts = hostname.split(".");
  if (parts.length <= 1) return variants;
  if (parts.length === 4 && Number.parseInt(parts[parts.length - 1], 10) > 0) {
    return variants; // IP address
  }
  for (let i = parts.length - 2; i >= 0; i--) {
    variants.push(parts.slice(i).join("."));
  }
  return variants;
}

/**
 * Synchronous, SDK-independent wipe of Segment's identity storage. Never
 * throws (a sandboxed frame's cookie access can — analytics-next guards the
 * same way).
 */
export function wipeSegmentIdentityStorage(): void {
  if (typeof window === "undefined") return;
  const domains = segmentCookieDomainVariants(window.location.hostname);
  for (const key of ALL_SEGMENT_IDENTITY_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage unavailable — the cookie half below still runs */
    }
    for (const domain of domains) {
      try {
        document.cookie = `${key}=; Max-Age=0; path=/${
          domain ? `; domain=${domain}` : ""
        }`;
      } catch {
        /* sandboxed cookie access */
      }
    }
  }
}

export function resetClientSegmentIdentity(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  // Synchronous wipe FIRST — guaranteed even if a navigation destroys
  // everything (buffered SDK queue included) a tick later.
  wipeSegmentIdentityStorage();
  // Every sign-out routes here; the next shopper must not inherit the balance.
  safeLocalStorage.removeItem(FSA_SPENDER_BALANCE_STORAGE_KEY);
  // Then the SDK reset for the loaded case (in-memory user + the `reset`
  // event the D6 belt consumes). Bounded/fire-and-forget at the callers —
  // the guarantee above no longer depends on it.
  return Promise.resolve(window.analytics?.reset());
}
